import { Typenames } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import useBelieveAccount from 'features/believe/application/hooks/useBelieveAccount';
import useWalletAvailability from 'features/believe/application/hooks/useWalletAvailability';
import useExternalsCards from 'features/myVaro/screens/ExternalAccounts/hooks/useExternalsCards';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useFlowReturnRoute from 'shared/hooks/useFlowReturnRoute';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import theme from 'shared/styles/theme';

import ApprovedScreenContent from './ApprovedScreenContent';

const ApprovedScreen = () => {
  const router = useRouter();
  const { cardId } = useLocalSearchParams<{ cardId: string }>();
  const { t: translatedText } = useTranslation('believe');
  const isWalletAvailable = useWalletAvailability();
  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BANKING,
  ]);
  const { account } = useBelieveAccount();
  const accountId = account?.id;
  const { externalCards } = useExternalsCards();
  const { exitToReturnRoute } = useFlowReturnRoute('believeApplication');

  useViewDidFocusScreenAnalyticsEvent({
    key: 'approvedBelieveApplication',
  });

  const handleAddMoneyAnalytics = usePressableAnalytics({
    analyticsId: 'addMoney',
  });

  const handleSkipAnalytics = usePressableAnalytics({
    analyticsId: 'skip',
  });

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const handleAddMoney = () => {
    handleAddMoneyAnalytics();
    const hasExternalCards =
      externalCards?.__typename === Typenames.ExternalCardsSuccess &&
      externalCards.externalCard.length > 0;

    const pathname = hasExternalCards
      ? '/believe-funding/tabapay/tabapay-transfer-screen'
      : '/believe-funding/tabapay/dcf-tabapay-form';

    const params = hasExternalCards
      ? {
          fromFundInstantly: 'true',
          isBelieveDay0Flow: 'true',
          believeCardId: cardId,
        }
      : {
          tabaPayEntryFlowType: 'DCF',
          isBelieveDay0Flow: 'true',
          believeCardId: cardId,
        };

    router.push({ pathname, params });
  };

  const handleSkip = () => {
    handleSkipAnalytics();
    if (cardId && isWalletAvailable) {
      router.push({
        pathname: '/believe/apply/addWallet',
        params: { cardId, isBelieveDay0Flow: 'true' },
      });
      return;
    }
    if (exitToReturnRoute()) {
      return;
    }
    if (accountId) {
      router.replace({
        pathname: '/accounts/[id]',
        params: { id: accountId },
      });
    } else {
      backToHome();
    }
  };

  const backToHome = () => {
    if (router.canGoBack()) {
      router.dismissAll();
    }
    router.back();
  };

  useBackHandler(backToHome);

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <NavigationBar
        title={undefined}
        empty
        trailingAccessories={
          <Button
            label={translatedText('application.approvedApplication.skip')}
            type="TEXT"
            onPress={handleSkip}
          />
        }
      />
      <ActionScreen
        visual="BELIEVE_DARK_CARD_A_ANIMATED"
        visualAccessibilityLabel={translatedText(
          'application.approvedApplication.cardAccessibilityLabel',
        )}
        titleText={translatedText('application.approvedApplication.title')}
        titleTextStyle="extra-large"
        bodyContent={<ApprovedScreenContent />}
        primaryButton={{
          label: translatedText('application.approvedApplication.addMoney'),
          onPress: handleAddMoney,
        }}
        includeDefaultPadding={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default ApprovedScreen;
